import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Typography, Tag, Row, Col, Empty, Button, Modal, message, List, Form, Input, Select, InputNumber, DatePicker, Switch, Tooltip, Alert } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LeftOutlined, RightOutlined, StopOutlined, UserAddOutlined, UserOutlined, DeleteOutlined, UserDeleteOutlined, BellOutlined, SwapOutlined, CopyOutlined, PlusOutlined, InfoCircleOutlined, BarChartOutlined, StarOutlined, WhatsAppOutlined, AppstoreOutlined, EditOutlined } from '@ant-design/icons';
import StatsInputModal from '../../components/organizer/StatsInputModal';
import CallRecipientsPopover from '../../components/organizer/CallRecipientsPopover';
import GameStatsBroadcastModal from '../../components/organizer/GameStatsBroadcastModal';
import RosterAdjustModal from '../../components/organizer/RosterAdjustModal';
import GameRatingsModal from '../../components/organizer/GameRatingsModal';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { FaFutbol, FaRunning } from 'react-icons/fa';
import { GiGloves } from 'react-icons/gi';
import api from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import axios from 'axios';

dayjs.locale('pt-br');

const { Title, Text } = Typography;

const OrganizerGames = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(4);
  const [teamConfig, setTeamConfig] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [newGameModalVisible, setNewGameModalVisible] = useState(false);
  const [newGameLoading, setNewGameLoading] = useState(false);
  const [newGameForm] = Form.useForm();
  // Editar jogo avulso
  const [editGameModal, setEditGameModal] = useState({ open: false, game: null });
  const [editGameLoading, setEditGameLoading] = useState(false);
  const [editGameForm] = Form.useForm();

  useEffect(() => {
    const updateCardsPerPage = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      let cols, rows;
      if (width >= 1200) {
        cols = 3;
        rows = height > 800 ? 2 : 1;
      } else if (width >= 768) {
        cols = 2;
        rows = height > 600 ? 2 : 1;
      } else {
        cols = 1;
        rows = height > 600 ? 3 : 2;
      }

      setCardsPerPage(cols * rows);
    };

    updateCardsPerPage();
    window.addEventListener('resize', updateCardsPerPage);
    return () => window.removeEventListener('resize', updateCardsPerPage);
  }, []);

  const fetchTeamConfig = async () => {
    try {
      const r = await api.get('/team-config');
      setTeamConfig(r.data);
    } catch (err) {
      console.error('erro team-config:', err);
    }
  };

  const toggleAutoOpen = async (checked) => {
    const previous = teamConfig?.open_game_automatically;
    setAutoLoading(true);
    // Optimistic UI
    setTeamConfig((prev) => prev ? { ...prev, open_game_automatically: checked ? 'SIM' : 'NAO' } : prev);
    try {
      const r = await api.put('/team-config', {
        ...teamConfig,
        open_game_automatically: checked,
      });
      const created = r.data?.next_game_created_message;
      if (created) {
        // Backend detectou NAO->SIM sem game ABERTO e ja criou o proximo.
        // Modal em vez de toast pra o organizador nao perder o aviso.
        Modal.success({
          title: 'Próximo jogo criado',
          content: created,
          okText: 'Entendi',
          onOk: () => fetchGames(),
        });
      } else {
        message.success(checked
          ? 'Jogos serão criados automaticamente após cada partida'
          : 'Criação automática desativada — crie os jogos manualmente'
        );
      }
    } catch (err) {
      // Rollback
      setTeamConfig((prev) => prev ? { ...prev, open_game_automatically: previous } : prev);
      message.error('Erro ao atualizar');
    } finally {
      setAutoLoading(false);
    }
  };

  const openNewGameModal = () => {
    newGameForm.resetFields();
    newGameForm.setFieldsValue({
      date_of_game: dayjs().add(1, 'day').hour(20).minute(0),
      players: teamConfig?.players,
      goalkeepers: teamConfig?.goalkeepers,
      // early_call_at, first_call_at, second_call_at deixamos undefined pro user escolher
    });
    setNewGameModalVisible(true);
  };

  const doCreateNewGame = async (values) => {
    setNewGameLoading(true);
    try {
      const fmt = (d) => d && dayjs.isDayjs(d) ? d.format('DD/MM/YYYY HH:mm') : undefined;
      const payload = {
        date_of_game: values.date_of_game.format('DD/MM/YYYY HH:mm'),
        players: values.players,
        goalkeepers: values.goalkeepers,
        early_call_at: fmt(values.early_call_at),
        first_call_at: fmt(values.first_call_at),
        second_call_at: fmt(values.second_call_at),
      };
      await api.post('/organizer-games', payload);
      message.success('Jogo criado com sucesso!');
      setNewGameModalVisible(false);
      fetchGames();
    } catch (err) {
      if (err.response?.status === 409) {
        message.error('Já existe jogo neste horário');
      } else {
        message.error(err.response?.data?.message || 'Erro ao criar jogo');
      }
    } finally {
      setNewGameLoading(false);
    }
  };

  // Helper: parse "DD/MM/YYYY HH:mm" -> dayjs (pra hidratar form de edição)
  const parseBrDateTime = (str) => {
    if (!str || typeof str !== 'string') return null;
    const m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{1,2}):(\d{2})/);
    if (!m) return null;
    return dayjs(`${m[3]}-${m[2]}-${m[1]}T${m[4].padStart(2, '0')}:${m[5]}`);
  };

  // Só é editável: jogo AVULSO (is_manual=true, gravado no create),
  // ABERTO, futuro.
  const isEditableGame = (game) => {
    if (!game) return false;
    if (game.status !== 'ABERTO' && game.status !== 'aberto') return false;
    if (!game.is_manual) return false;
    const gameMs = parseBrDateTime(game.date_of_game)?.valueOf();
    if (!gameMs || gameMs < Date.now()) return false;
    return true;
  };

  const openEditGame = (game) => {
    editGameForm.resetFields();
    editGameForm.setFieldsValue({
      date_of_game: parseBrDateTime(game.date_of_game),
      early_call_at: parseBrDateTime(game.date_early_call),
      first_call_at: parseBrDateTime(game.date_first_call),
      second_call_at: parseBrDateTime(game.date_second_call),
    });
    setEditGameModal({ open: true, game });
  };

  const submitEditGame = async (values) => {
    const g = editGameModal.game;
    if (!g) return;
    setEditGameLoading(true);
    try {
      const fmt = (d) => d && dayjs.isDayjs(d) ? d.format('DD/MM/YYYY HH:mm') : null;
      // Só envia campo se mudou (evita bloqueio de "convocação já enviada"
      // quando o form re-envia o valor original de uma call já enviada)
      const payload = {};
      const cur = {
        date_of_game: g.date_of_game || null,
        early_call_at: g.date_early_call || null,
        first_call_at: g.date_first_call || null,
        second_call_at: g.date_second_call || null,
      };
      const next = {
        date_of_game: fmt(values.date_of_game),
        early_call_at: fmt(values.early_call_at),
        first_call_at: fmt(values.first_call_at),
        second_call_at: fmt(values.second_call_at),
      };
      for (const k of Object.keys(next)) {
        if ((cur[k] || null) !== (next[k] || null)) payload[k] = next[k];
      }
      if (Object.keys(payload).length === 0) {
        message.info('Nada mudou');
        setEditGameModal({ open: false, game: null });
        return;
      }
      await api.put(`/organizer-games/${g.id}`, payload);
      message.success('Jogo atualizado!');
      setEditGameModal({ open: false, game: null });
      fetchGames();
    } catch (err) {
      message.error(err.response?.data?.message || 'Erro ao editar jogo');
    } finally {
      setEditGameLoading(false);
    }
  };

  const handleCreateNewGame = async (values) => {
    // Alerta se 2+ convocacoes caem no MESMO dia. WhatsApp trata multiplas
    // msgs pro mesmo grupo/dia como spam e pode banir o chip. Nao bloqueia,
    // mas exige confirmacao consciente do organizador.
    const dias = [values.early_call_at, values.first_call_at, values.second_call_at]
      .filter(d => d && dayjs.isDayjs(d))
      .map(d => d.format('YYYY-MM-DD'));
    const diasUnicos = new Set(dias);
    if (dias.length >= 2 && diasUnicos.size < dias.length) {
      Modal.confirm({
        title: 'Mais de uma convocação no mesmo dia',
        content: (
          <div>
            <p>Você tá cadastrando <strong>{dias.length} convocações</strong> caindo em <strong>{diasUnicos.size} dia{diasUnicos.size > 1 ? 's' : ''}</strong>.</p>
            <p>O WhatsApp costuma bloquear envio de mais de uma convocação no mesmo dia pro mesmo grupo (interpreta como spam) e pode banir o chip.</p>
            <p><strong>Recomendação: uma convocação por dia.</strong></p>
            <p>Quer prosseguir mesmo assim?</p>
          </div>
        ),
        okText: 'Sim, cadastrar assim',
        cancelText: 'Ajustar as datas',
        okButtonProps: { danger: true },
        onOk: () => doCreateNewGame(values),
      });
      return;
    }
    await doCreateNewGame(values);
  };

  const fetchGames = async () => {
    setLoading(true);
    try {
      const response = await api.get('/organizer-games');
      setGames(response.data);
    } catch (error) {
      console.error('Erro ao carregar jogos');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchGames();
    fetchTeamConfig();
  }, []);

  const renderCallStatus = (sent) => {
    return sent ? (
      <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
    ) : (
      <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />
    );
  };

  const handleCancelGame = (gameId) => {
    // Abre modal customizado (checkbox notificar + textarea justificativa)
    // Substitui o Modal.confirm anterior porque precisa componentes controlados.
    setCancelModal({ open: true, gameId, notify: true, reason: '', cooldownInfo: null });
    // Puxa status de cooldown em paralelo
    api.get(`/organizer-games/${gameId}/cancel-notify-status`)
      .then(r => setCancelModal(prev => prev.open && prev.gameId === gameId
        ? { ...prev, cooldownInfo: r.data }
        : prev))
      .catch(() => { /* silencioso — se falhar, assumimos que pode enviar */ });
  };

  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [selectedGameId, setSelectedGameId] = useState(null);
  const [cancelModal, setCancelModal] = useState({ open: false, gameId: null, notify: true, reason: '', cooldownInfo: null });
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [unconfirmedPlayers, setUnconfirmedPlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [externalPlayerModalVisible, setExternalPlayerModalVisible] = useState(false);
  const [externalPlayerForm] = Form.useForm();
  const [externalPlayerLoading, setExternalPlayerLoading] = useState(false);
  const [removePlayerModalVisible, setRemovePlayerModalVisible] = useState(false);
  const [confirmedPlayers, setConfirmedPlayers] = useState([]);
  const [loadingConfirmedPlayers, setLoadingConfirmedPlayers] = useState(false);
  const [manualCallModalVisible, setManualCallModalVisible] = useState(false);
  const [selectedGameForCall, setSelectedGameForCall] = useState(null);
  const [statsModal, setStatsModal] = useState({ open: false, gameId: null, gameLabel: '' });
  const [ratingsModal, setRatingsModal] = useState({ open: false, gameId: null, gameLabel: '' });
  const [statsBroadcastModal, setStatsBroadcastModal] = useState({ open: false, gameId: null, gameLabel: '' });
  const [rosterAdjustModal, setRosterAdjustModal] = useState({ open: false, gameId: null, gameLabel: '' });
  const [callInfo, setCallInfo] = useState(null);
  const [loadingCallInfo, setLoadingCallInfo] = useState(false);
  const [sendingManualCall, setSendingManualCall] = useState(false);
  const [sortModalVisible, setSortModalVisible] = useState(false);
  const [selectedGameForSort, setSelectedGameForSort] = useState(null);
  const [sortBy, setSortBy] = useState('teams'); // 'teams' ou 'playersPerTeam'
  const [numberOfTeams, setNumberOfTeams] = useState(2);
  const [playersPerTeam, setPlayersPerTeam] = useState(6);
  const [sortedTeams, setSortedTeams] = useState([]);
  const [loadingSort, setLoadingSort] = useState(false);

  const handleConfirmPlayer = async (gameId) => {
    setSelectedGameId(gameId);
    setConfirmModalVisible(true);
    setLoadingPlayers(true);

    try {
      const response = await api.get(`/organizer-unconfirmed-players/${gameId}`);
      setUnconfirmedPlayers(response.data);
    } catch (error) {
      message.error('Erro ao carregar jogadores');
    }
    setLoadingPlayers(false);
  };

  const handleConfirmPlayerManually = (player) => {
    Modal.confirm({
      title: 'Confirmar Jogador',
      content: `Confirma a confirmação manual do jogador "${player.name}"?`,
      okText: 'Sim, confirmar',
      cancelText: 'Não',
      onOk: async () => {
        try {
          const response = await axios.post('https://automacao.mooblab.com/webhook/confirm-manually-play-v2', {
            game_id: selectedGameId,
            customer_id: user.customer_id,
            player_id: player.id
          }, {
            headers: {
              'futezap-admin-api-token': ':Wu|_-1;)n0HJF)e@£5Mqm'
            }
          });

          if (response.data.success === 'true' || response.data.success === true) {
            message.success(`Jogador ${player.name} confirmado com sucesso!`);
            // Recarregar lista de jogadores não confirmados
            const updatedResponse = await api.get(`/organizer-unconfirmed-players/${selectedGameId}`);
            setUnconfirmedPlayers(updatedResponse.data);
            fetchGames(); // Recarregar jogos
          } else {
            message.error('Erro ao confirmar jogador');
          }
        } catch (error) {
          console.error('Erro ao confirmar jogador:', error);
          message.error('Erro ao confirmar jogador');
        }
      }
    });
  };

  const handleAddExternalPlayer = (gameId) => {
    setSelectedGameId(gameId);
    setExternalPlayerModalVisible(true);
    externalPlayerForm.resetFields();
  };

  const handleExternalPlayerSubmit = async (values) => {
    setExternalPlayerLoading(true);
    try {
      const response = await axios.post('https://automacao.mooblab.com/webhook/add-external-player', {
        game_id: selectedGameId,
        customer_id: user.customer_id,
        name: values.name,
        position: values.position,
        technical_level_external: values.technical_level
      }, {
        headers: {
          'futezap-admin-api-token': ':Wu|_-1;)n0HJF)e@£5Mqm'
        }
      });

      if (response.data.success === 'true' || response.data.success === true) {
        message.success('Jogador convidado adicionado com sucesso!');
        setExternalPlayerModalVisible(false);
        fetchGames();
      } else {
        message.error('Erro ao adicionar jogador convidado');
      }
    } catch (error) {
      console.error('Erro ao adicionar jogador convidado:', error);
      message.error('Erro ao adicionar jogador convidado');
    } finally {
      setExternalPlayerLoading(false);
    }
  };

  const canSendManualCall = (game) => {
    // Verificar se o time está sincronizado (tem groupjid)
    if (!user?.groupjid) {
      return false;
    }
    
    // Verificar se pelo menos uma convocação ainda não foi enviada
    return !game.sent_early_call || !game.sent_first_call || !game.sent_second_call || !game.sent_third_call;
  };

  const handleManualCall = async (game) => {
    setSelectedGameForCall(game);
    setManualCallModalVisible(true);
    setLoadingCallInfo(true);
    
    try {
      // Verificar se já foi enviada convocação hoje
      const response = await api.get(`/organizer-games/${game.id}/call-info`);
      setCallInfo(response.data);
    } catch (error) {
      console.error('Erro ao carregar informações da convocação:', error);
      message.error('Erro ao carregar informações da convocação');
    }
    setLoadingCallInfo(false);
  };

  // Parse formato "DD/MM/YYYY HH:mm" pra Date (BR)
  const parseBrDate = (s) => {
    if (!s || s === 'null') return null;
    const [datePart, timePart] = String(s).split(' ');
    if (!datePart) return null;
    const [dd, mm, yyyy] = datePart.split('/').map(Number);
    const [hh, mi] = (timePart || '00:00').split(':').map(Number);
    if (!dd || !mm || !yyyy) return null;
    return new Date(yyyy, mm - 1, dd, hh || 0, mi || 0);
  };

  const handleConfirmManualCall = async () => {
    // Guard anti-double-click: se ja esta processando, ignora clique.
    // Complementa o loading={sendingManualCall} do botao — protege contra
    // Enter/space bar/duplo clique rapidissimo antes do React re-render.
    if (sendingManualCall) return;

    // Verificar se não é localhost
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalhost) {
      message.warning('Envio de convocação não disponível em ambiente local');
      return;
    }

    // Bloqueia envio antes do horário programado da primeira convocação
    const earlyAt = parseBrDate(selectedGameForCall?.date_early_call);
    if (earlyAt && new Date() < earlyAt) {
      message.warning(`Aguarde até ${selectedGameForCall.date_early_call} para enviar a convocação. Antes disso, os jogadores não conseguem confirmar.`, 8);
      return;
    }

    setSendingManualCall(true);
    try {
      // Backend faz o proxy pro n8n + dispara push notification pros community_players do time
      const response = await api.post(`/organizer-games/${selectedGameForCall.id}/send-manual-call`, {
        game_name: selectedGameForCall.name,
      });

      if (response.data.success) {
        const pushInfo = response.data.push;
        const pushMsg = pushInfo?.sent ? ` (${pushInfo.sent} ${pushInfo.sent > 1 ? 'notificações' : 'notificação'} push enviada${pushInfo.sent > 1 ? 's' : ''})` : '';
        message.success(`Convocação enviada com sucesso!${pushMsg}`);
        setManualCallModalVisible(false);
        fetchGames();
      } else {
        message.error('Erro ao enviar convocação');
      }
    } catch (error) {
      console.error('Erro ao enviar convocação:', error);
      const errBody = error?.response?.data;
      if (error?.response?.status === 429) {
        message.warning(errBody?.mensagem || 'Aguarde antes de reenviar essa convocação', 6);
      } else {
        message.error(errBody?.mensagem || errBody?.error || 'Erro ao enviar convocação');
      }
    } finally {
      setSendingManualCall(false);
    }
  };

  const getPlayerTypeText = (type) => {
    if (type === 1) return 'Somente mensalistas';
    if (type === 2) return 'Mensalistas e avulsos';
    return 'Não definido';
  };

  const handleSort = async (game) => {
    setSelectedGameForSort(game);
    setSortModalVisible(true);
    setSortBy('teams');
    setNumberOfTeams(2);
    setPlayersPerTeam(6);
    setSortedTeams([]);
  };

  const handleSortTeams = async () => {
    setLoadingSort(true);
    try {
      // Buscar jogadores do jogo
      const playersResponse = await api.get(`/game-sort/${selectedGameForSort.id}/players`);
      const players = playersResponse.data;

      if (players.length === 0) {
        message.warning('Nenhum jogador confirmado para sortear');
        setLoadingSort(false);
        return;
      }

      // Sortear times
      const sortResponse = await api.post('/game-sort/sort', {
        players,
        sortBy,
        numberOfTeams,
        playersPerTeam
      });

      setSortedTeams(sortResponse.data.teams);
    } catch (error) {
      console.error('Erro ao sortear times:', error);
      message.error('Erro ao sortear times');
    }
    setLoadingSort(false);
  };

  const handleSendToGroup = async () => {
    try {
      // Formatar dados no formato do N8N
      const emojis = ['🔵', '🔴', '🟢', '🟡', '🟣', '🟤', '⚫', '⚪', '🟥', '🟧'];
      let mensagem = '🏆 *Times sorteados:*\\n\\n';
      
      const timesObject = {};
      sortedTeams.forEach((team, index) => {
        const emoji = emojis[index % emojis.length];
        const timeNome = `time${index + 1}`;
        
        mensagem += `${emoji} *${team.name}:*\\n`;
        team.players.forEach(player => {
          const tipo = player.type === 2 ? ' 🧤' : '';
          mensagem += `- ${player.name}${tipo}\\n`;
        });
        mensagem += '\\n';
        
        timesObject[timeNome] = team.players;
      });
      
      const payload = {
        customer_id: user.customer_id,
        game_id: selectedGameForSort.id,
        mensagem,
        times: timesObject
      };
      
      const response = await fetch('https://automacao.mooblab.com/webhook/send-drawing-to-group', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'futezap-admin-api-token': ':Wu|_-1;)n0HJF)e@£5Mqm'
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (data.success === 'true' || data.success === true) {
        message.success('Times enviados para o grupo com sucesso!');
      } else {
        message.error('Erro ao enviar times para o grupo');
      }
    } catch (error) {
      console.error('Erro ao enviar para o grupo:', error);
      message.error('Erro ao enviar para o grupo');
    }
  };

  const formatTeamsForWhatsApp = () => {
    if (sortedTeams.length === 0) return '';
    
    let text = '🏆 *Times sorteados:*\n\n';
    
    const emojis = ['🔵', '🔴', '🟢', '🟡', '🟣', '🟤', '⚫', '⚪', '🟥', '🟧'];
    
    sortedTeams.forEach((team, index) => {
      const emoji = emojis[index % emojis.length];
      text += `${emoji} *${team.name}:*\n`;
      
      team.players.forEach(player => {
        const goleiro = player.type === 2 ? ' 🧤' : '';
        text += `- ${player.name}${goleiro}\n`;
      });
      
      text += '\n';
    });
    
    return text;
  };

  const handleCopyTeam = async (team) => {
    const emojis = ['🔵', '🔴', '🟢', '🟡', '🟣', '🟤', '⚫', '⚪', '🟥', '🟧'];
    const teamIndex = sortedTeams.findIndex(t => t.name === team.name);
    const emoji = emojis[teamIndex % emojis.length];
    
    let text = `${emoji} *${team.name}:*\n`;
    
    team.players.forEach(player => {
      const goleiro = player.type === 2 ? ' 🧤' : '';
      text += `- ${player.name}${goleiro}\n`;
    });
    
    try {
      await navigator.clipboard.writeText(text);
      message.success(`${team.name} copiado!`);
    } catch (error) {
      message.error('Erro ao copiar time');
    }
  };

  const handleCopyAllTeams = async () => {
    try {
      await navigator.clipboard.writeText(formatTeamsForWhatsApp());
      message.success('Todos os times copiados!');
    } catch (error) {
      message.error('Erro ao copiar times');
    }
  };

  const handleRemovePlayer = async (gameId) => {
    setSelectedGameId(gameId);
    setRemovePlayerModalVisible(true);
    setLoadingConfirmedPlayers(true);

    try {
      const response = await api.get(`/organizer-confirmed-players/${gameId}`);
      setConfirmedPlayers(response.data);
    } catch (error) {
      message.error('Erro ao carregar jogadores confirmados');
    }
    setLoadingConfirmedPlayers(false);
  };

  const handleRemovePlayerConfirm = (player) => {
    Modal.confirm({
      title: 'Remover Jogador',
      content: `Tem certeza que deseja remover "${player.name}" do jogo?`,
      okText: 'Sim, remover',
      cancelText: 'Não',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await axios.post('https://automacao.mooblab.com/webhook/remove-game-player', {
            game_id: selectedGameId,
            customer_id: user.customer_id,
            game_player_id: player.game_player_id
          }, {
            headers: {
              'futezap-admin-api-token': ':Wu|_-1;)n0HJF)e@£5Mqm'
            }
          });

          if (response.data.success === 'true' || response.data.success === true) {
            message.success(`Jogador ${player.name} removido com sucesso!`);
            const updatedResponse = await api.get(`/organizer-confirmed-players/${selectedGameId}`);
            setConfirmedPlayers(updatedResponse.data);
            fetchGames();
          } else {
            message.error('Erro ao remover jogador');
          }
        } catch (error) {
          console.error('Erro ao remover jogador:', error);
          message.error('Erro ao remover jogador');
        }
      }
    });
  };

  const renderGameCard = (game) => {
    const isClosed = game.status === 'FECHADO';
    return (
    <div style={{ width: '100%' }}>
      <Card
        style={{
          height: window.innerWidth <= 768 ? '' : 'auto',
          minHeight: window.innerWidth <= 768 ? '' : '420px',
          maxWidth: '400px',
          // FECHADO usa border cinza clara + tint sutil no background pra diferenciar,
          // sem opacity (opacity no card pai forca filhos e nao pode ser sobrescrito).
          border: isClosed ? '1px solid #e8e8e8' : '1px solid #40a9ff',
          background: isClosed ? '#fafafa' : '#fff',
        }}
        styles={{
          header: {
            padding: '12px'
          },
          body: {
            padding: '12px'
          }
        }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaFutbol style={{ color: '#fa8c16' }} />
            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{game.name}</span>
          </div>
        }
        extra={<Tag color={game.status === 'ATIVO' ? 'green' : 'default'} style={{ fontSize: '11px', padding: '2px 6px' }}>{game.status}</Tag>}
      >
        <div style={{ marginTop: '5px' }}>
        <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
          <Col span={12}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaRunning style={{ color: '#1890ff' }} />
              <Text strong>Jogadores:</Text> {game.players || 0}
            </div>
          </Col>
          <Col span={12}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <GiGloves style={{ color: '#52c41a' }} />
              <Text strong>Goleiros:</Text> {game.goalkeepers || 0}
            </div>
          </Col>
        </Row>
        <div style={{ marginBottom: 8 }}>
          <Text strong>Status dos Jogadores:</Text>
          <Row gutter={[4, 4]} style={{ marginTop: 4 }}>
            <Col span={12}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary">Vai jogar:</Text>
                <Tag color="green">{game.vai_jogar || 0}</Tag>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary">Fora:</Text>
                <Tag color="red">{game.fora_do_jogo || 0}</Tag>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary">Espera:</Text>
                <Tag color="orange">{game.espera || 0}</Tag>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary">Não confirmado:</Text>
                <Tag color="default">{game.nao_confirmado || 0}</Tag>
              </div>
            </Col>
          </Row>
        </div>

        <div>
          <Text strong>Convocações:</Text>
          <div style={{ marginTop: 4 }}>
            <Row gutter={[4, 4]}>
              <Col span={20}>
                <Text type="secondary">1ª:</Text> {game.date_early_call && game.date_early_call !== 'null' ? game.date_early_call : ''}
              </Col>
              <Col span={4} style={{ textAlign: 'center' }}>
                <CallRecipientsPopover gameId={game.id} callType="EARLY_CALL" sent={game.sent_early_call}>
                  {renderCallStatus(game.sent_early_call)}
                </CallRecipientsPopover>
              </Col>
            </Row>
            <Row gutter={[4, 4]}>
              <Col span={20}>
                <Text type="secondary">2ª:</Text> {game.date_first_call && game.date_first_call !== 'null' ? game.date_first_call : ''}
              </Col>
              <Col span={4} style={{ textAlign: 'center' }}>
                <CallRecipientsPopover gameId={game.id} callType="FIRST_CALL" sent={game.sent_first_call}>
                  {renderCallStatus(game.sent_first_call)}
                </CallRecipientsPopover>
              </Col>
            </Row>
            <Row gutter={[4, 4]}>
              <Col span={20}>
                <Text type="secondary">3ª:</Text> {game.date_second_call && game.date_second_call !== 'null' ? game.date_second_call : ''}
              </Col>
              <Col span={4} style={{ textAlign: 'center' }}>
                <CallRecipientsPopover gameId={game.id} callType="SECOND_CALL" sent={game.sent_second_call}>
                  {renderCallStatus(game.sent_second_call)}
                </CallRecipientsPopover>
              </Col>
            </Row>
            <Row gutter={[4, 4]}>
              <Col span={20}>
                <Text type="secondary">4ª:</Text> {game.date_third_call && game.date_third_call !== 'null' ? game.date_third_call : ''}
              </Col>
              <Col span={4} style={{ textAlign: 'center' }}>
                <CallRecipientsPopover gameId={game.id} callType="THIRD_CALL" sent={game.sent_third_call}>
                  {renderCallStatus(game.sent_third_call)}
                </CallRecipientsPopover>
              </Col>
            </Row>
          </div>
        </div>

        {/* Debug: mostrar status atual */}
        <div style={{ marginTop: 6, fontSize: '12px', color: '#666' }}>
          Status atual: "{game.status}"
        </div>

        {(isClosed || game.status === 'ABERTO' || game.status === 'aberto' || game.status === 'ATIVO' || game.status === 'ativo') && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Button
              block
              type="primary"
              icon={<BarChartOutlined />}
              size="middle"
              style={{
                height: 42, fontWeight: 700, fontSize: 14,
                background: '#1890ff', borderColor: '#1890ff',
                boxShadow: '0 4px 12px rgba(24, 144, 255, 0.35)',
              }}
              onClick={() => setStatsModal({ open: true, gameId: game.id, gameLabel: game.name })}
            >
              {isClosed ? 'Lançar estatísticas' : 'Lançar estatísticas (durante o jogo)'}
            </Button>
            <Button
              block
              icon={<StarOutlined />}
              size="middle"
              style={{
                height: 38, fontWeight: 600, fontSize: 13,
                color: '#faad14', borderColor: '#faad14',
              }}
              onClick={() => setRatingsModal({ open: true, gameId: game.id, gameLabel: game.name })}
            >
              Ver avaliações
            </Button>
            <Button
              block
              icon={<SwapOutlined />}
              size="middle"
              style={{ height: 38, fontWeight: 500, fontSize: 13, color: '#595959' }}
              onClick={() => setRosterAdjustModal({ open: true, gameId: game.id, gameLabel: game.name })}
              title="Corrige quem realmente jogou pra avaliação (sem enviar msg no grupo)"
            >
              Ajustar lista (só p/ avaliação)
            </Button>
            {isClosed && (
              <Button
                block
                icon={<WhatsAppOutlined />}
                size="middle"
                style={{
                  height: 38, fontWeight: 600, fontSize: 13,
                  background: '#25D366', borderColor: '#25D366', color: '#fff',
                }}
                onClick={() => setStatsBroadcastModal({ open: true, gameId: game.id, gameLabel: game.name })}
              >
                Enviar ranking no grupo
              </Button>
            )}
          </div>
        )}

        {(game.status === 'ATIVO' || game.status === 'ativo' || game.status === 'ABERTO' || game.status === 'aberto') && (
          <div style={{ marginTop: 12 }}>
            {isEditableGame(game) && (
              <div style={{ marginBottom: 8 }}>
                <Button
                  block
                  icon={<EditOutlined />}
                  size="small"
                  style={{ height: '28px' }}
                  onClick={() => openEditGame(game)}
                >
                  Editar jogo (data + convocações)
                </Button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <Button
                style={{ minWidth: '140px', height: '28px' }}
                type="primary"
                danger
                icon={<StopOutlined />}
                size="small"
                onClick={() => handleCancelGame(game.id)}
              >
                Cancelar
              </Button>
              <Button
                style={{ minWidth: '140px', height: '28px' }}
                type="primary"
                icon={<UserAddOutlined />}
                size="small"
                onClick={() => handleConfirmPlayer(game.id)}
              >
                Confirmar
              </Button>
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <Button
                style={{ minWidth: '140px', height: '28px' }}
                type="default"
                icon={<UserOutlined />}
                size="small"
                onClick={() => handleAddExternalPlayer(game.id)}
              >
                Convidado
              </Button>
              <Button
                style={{ minWidth: '140px', height: '28px' }}
                type="default"
                danger
                icon={<UserDeleteOutlined />}
                size="small"
                onClick={() => handleRemovePlayer(game.id)}
              >
                Remover
              </Button>
            </div>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {canSendManualCall(game) && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: window.innerWidth <= 768 ? 'space-between' : 'center'
                }}>
                  <Button
                    style={{ 
                      width: window.innerWidth <= 768 ? '100%' : '320px',
                      height: '28px' 
                    }}
                    type="primary"
                    ghost
                    icon={<BellOutlined />}
                    size="small"
                    onClick={() => handleManualCall(game)}
                  >
                    Convocação Manual
                  </Button>
                </div>
              )}
              <div style={{ 
                display: 'flex', 
                justifyContent: window.innerWidth <= 768 ? 'space-between' : 'center'
              }}>
                <Button
                  style={{ 
                    width: window.innerWidth <= 768 ? '100%' : '320px',
                    height: '28px' 
                  }}
                  type="default"
                  icon={<SwapOutlined />}
                  size="small"
                  onClick={() => handleSort(game)}
                >
                  Sortear Times
                </Button>
              </div>
            </div>
          </div>
        )}
        </div>
      </Card>
    </div>
    );
  };

  const isAutoOpen = teamConfig?.open_game_automatically !== 'NAO'; // trata null/undefined como SIM (default)

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
        <Col><Title level={2} style={{ margin: 0 }}>Jogos</Title></Col>
        <Col>
          <Row gutter={[16, 8]} align="middle" wrap>
            <Col>
              <Tooltip
                title={isAutoOpen
                  ? 'Após cada jogo, o próximo é aberto automaticamente'
                  : 'Você cria cada jogo manualmente — bom pra times que não jogam toda semana ou mudam o dia'}
              >
                <span>
                  <InfoCircleOutlined style={{ marginRight: 6, color: '#999' }} />
                  <Text style={{ marginRight: 8 }}>Abrir jogos automaticamente:</Text>
                  <Switch
                    checked={isAutoOpen}
                    onChange={toggleAutoOpen}
                    loading={autoLoading}
                    checkedChildren="SIM"
                    unCheckedChildren="NÃO"
                  />
                </span>
              </Tooltip>
            </Col>
            <Col>
              <Button type="primary" icon={<PlusOutlined />} onClick={openNewGameModal}>
                Novo jogo
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>

      {!isAutoOpen && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Criação automática desativada"
          description="Nenhum jogo será criado sozinho. Use o botão 'Novo jogo' pra criar cada partida no dia que quiser."
        />
      )}

      {loading ? (
        <Card loading={true} style={{ height: 'auto' }} />
      ) : games.length > 0 ? (
        <div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16, gap: 8 }}>
            <Button
              icon={<LeftOutlined />}
              onClick={() => setCurrentIndex(Math.max(0, currentIndex - cardsPerPage))}
              disabled={currentIndex === 0}
            />
            <Button
              icon={<RightOutlined />}
              onClick={() => setCurrentIndex(Math.min(games.length - cardsPerPage, currentIndex + cardsPerPage))}
              disabled={currentIndex >= games.length - cardsPerPage}
            />
          </div>
          <Row gutter={[16, 16]}>
            {games.slice(currentIndex, currentIndex + cardsPerPage).map(game => (
              <Col key={game.id} xs={24} sm={12} lg={8} style={{ display: 'flex', marginBottom: 16 }}>
                {renderGameCard(game)}
              </Col>
            ))}
          </Row>
        </div>
      ) : (
        <Empty description="Nenhum jogo encontrado" />
      )}

      <Modal
        title="Confirmar Jogadores"
        open={confirmModalVisible}
        onCancel={() => setConfirmModalVisible(false)}
        footer={null}
        width={600}
      >
        <List
          loading={loadingPlayers}
          dataSource={unconfirmedPlayers}
          renderItem={(player) => (
            <List.Item
              actions={[
                <Button
                  type="primary"
                  size="small"
                  onClick={() => handleConfirmPlayerManually(player)}
                >
                  Confirmar
                </Button>
              ]}
            >
              <List.Item.Meta
                title={player.name}
                description={player.phone_number}
              />
            </List.Item>
          )}
          locale={{ emptyText: 'Todos os jogadores já confirmaram' }}
        />
      </Modal>

      <Modal
        title="Adicionar Jogador Convidado"
        open={externalPlayerModalVisible}
        onCancel={() => setExternalPlayerModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form form={externalPlayerForm} onFinish={handleExternalPlayerSubmit} layout="vertical">
          <Form.Item name="name" label="Nome" rules={[{ required: true, message: 'Nome é obrigatório' }]}>
            <Input placeholder="Nome do jogador" />
          </Form.Item>
          <Form.Item name="position" label="Posição" rules={[{ required: true, message: 'Posição é obrigatória' }]}>
            <Select placeholder="Selecione a posição">
              <Select.Option value="1">Jogador de Linha</Select.Option>
              <Select.Option value="2">Goleiro</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="technical_level" label="Nível Técnico" rules={[{ required: true, message: 'Nível técnico é obrigatório' }]}>
            <InputNumber min={1} max={10} placeholder="1-10" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button onClick={() => setExternalPlayerModalVisible(false)} disabled={externalPlayerLoading}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={externalPlayerLoading}>
                {externalPlayerLoading ? 'Processando...' : 'Adicionar'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Remover Jogadores"
        open={removePlayerModalVisible}
        onCancel={() => setRemovePlayerModalVisible(false)}
        footer={null}
        width={600}
      >
        <List
          loading={loadingConfirmedPlayers}
          dataSource={confirmedPlayers}
          renderItem={(player) => (
            <List.Item
              actions={[
                <Button
                  type="primary"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                  onClick={() => handleRemovePlayerConfirm(player)}
                >
                  Remover
                </Button>
              ]}
            >
              <List.Item.Meta
                title={player.name}
                description={player.phone_number}
              />
            </List.Item>
          )}
          locale={{ emptyText: 'Nenhum jogador confirmado' }}
        />
      </Modal>

      <Modal
        title="Enviar Convocação Manual"
        open={manualCallModalVisible}
        onCancel={() => { if (!sendingManualCall) setManualCallModalVisible(false); }}
        maskClosable={!sendingManualCall}
        closable={!sendingManualCall}
        footer={(() => {
          const earlyAt = parseBrDate(selectedGameForCall?.date_early_call);
          const tooEarly = earlyAt && new Date() < earlyAt;
          return [
            <Button key="cancel" onClick={() => setManualCallModalVisible(false)}>
              Cancelar
            </Button>,
            <Button
              key="confirm"
              type="primary"
              onClick={handleConfirmManualCall}
              disabled={callInfo?.canSend === false || tooEarly || sendingManualCall}
              loading={sendingManualCall}
              title={tooEarly ? `Aguarde até ${selectedGameForCall.date_early_call}` : undefined}
            >
              {sendingManualCall ? 'Enviando...' : (tooEarly ? `Aguarde ${selectedGameForCall.date_early_call}` : 'Confirmar Envio')}
            </Button>
          ];
        })()}
        width={600}
      >
        {(() => {
          const earlyAt = parseBrDate(selectedGameForCall?.date_early_call);
          const tooEarly = earlyAt && new Date() < earlyAt;
          if (!tooEarly) return null;
          return (
            <div style={{ backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6, padding: 16, marginBottom: 16 }}>
              <Text strong style={{ color: '#d46b08' }}>
                ⚠️ Ainda não é hora de convocar
              </Text>
              <div style={{ marginTop: 8 }}>
                A primeira convocação está agendada para <Text strong>{selectedGameForCall.date_early_call}</Text>.
                Se disparar agora, os jogadores recebem a mensagem mas o sistema não deixa eles confirmarem antes desse horário — vira problema.
                Aguarde e envie no momento certo.
              </div>
            </div>
          );
        })()}
        {loadingCallInfo ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            Carregando informações...
          </div>
        ) : callInfo ? (
          <div>
            {!callInfo.canSend ? (
              <div style={{ 
                backgroundColor: '#fff2e8', 
                border: '1px solid #ffbb96', 
                borderRadius: 6, 
                padding: 16, 
                marginBottom: 16 
              }}>
                <Text strong style={{ color: '#d4380d' }}>
                  ⚠️ Todas as convocações já foram enviadas para este jogo.
                </Text>
              </div>
            ) : (
              <div>
                <div style={{ 
                  backgroundColor: '#f6ffed', 
                  border: '1px solid #b7eb8f', 
                  borderRadius: 6, 
                  padding: 16, 
                  marginBottom: 16 
                }}>
                  <Text strong style={{ color: '#389e0d' }}>
                    ℹ️ Ao enviar manualmente, esta convocação não será enviada novamente de forma automática.
                  </Text>
                  <br />
                  <Text style={{ color: '#389e0d' }}>
                    📱 Será enviado somente para jogadores que ainda não receberam ou não confirmaram presença no jogo.
                  </Text>
                  {callInfo.dailyCallsCount > 0 && (
                    <>
                      <br />
                      <Text style={{ color: '#fa8c16' }}>
                        📅 Já foram enviadas {callInfo.dailyCallsCount} convocação(s) hoje.
                      </Text>
                    </>
                  )}
                </div>
                
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Programação das Convocações:</Text>
                  <div style={{ marginTop: 8 }}>
                    <Row gutter={[8, 8]}>
                      <Col span={12}>
                        <Text type="secondary">1ª Convocação:</Text>
                      </Col>
                      <Col span={12}>
                        <Text>{(callInfo.dates?.early_call && callInfo.dates.early_call !== 'null') ? callInfo.dates.early_call : ''}</Text>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary">2ª Convocação:</Text>
                      </Col>
                      <Col span={12}>
                        <Text>{(callInfo.dates?.first_call && callInfo.dates.first_call !== 'null') ? callInfo.dates.first_call : ''}</Text>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary">3ª Convocação:</Text>
                      </Col>
                      <Col span={12}>
                        <Text>{(callInfo.dates?.second_call && callInfo.dates.second_call !== 'null') ? callInfo.dates.second_call : ''}</Text>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary">4ª Convocação:</Text>
                      </Col>
                      <Col span={12}>
                        <Text>{(callInfo.dates?.third_call && callInfo.dates.third_call !== 'null') ? callInfo.dates.third_call : ''}</Text>
                      </Col>
                    </Row>
                  </div>
                </div>
                
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Tipos de Jogadores que Receberão:</Text>
                  <div style={{ marginTop: 8 }}>
                    <Row gutter={[8, 8]}>
                      <Col span={12}>
                        <Text type="secondary">1ª Convocação:</Text>
                      </Col>
                      <Col span={12}>
                        <Text>Somente mensalistas</Text>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary">2ª Convocação:</Text>
                      </Col>
                      <Col span={12}>
                        <Text>{getPlayerTypeText(callInfo.playerTypes?.first_call)}</Text>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary">3ª Convocação:</Text>
                      </Col>
                      <Col span={12}>
                        <Text>{getPlayerTypeText(callInfo.playerTypes?.second_call)}</Text>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary">4ª Convocação:</Text>
                      </Col>
                      <Col span={12}>
                        <Text>{getPlayerTypeText(callInfo.playerTypes?.third_call)}</Text>
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </Modal>

      <Modal
        title="Sortear Times"
        open={sortModalVisible}
        onCancel={() => setSortModalVisible(false)}
        footer={null}
        width={800}
      >
        <div style={{ 
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontWeight: 'bold' }}>Sortear por:</label>
            <Select
              value={sortBy}
              onChange={setSortBy}
              style={{ width: window.innerWidth <= 768 ? 180 : 200 }}
            >
              <Select.Option value="teams">Número de times</Select.Option>
              <Select.Option value="playersPerTeam">Jogadores por time</Select.Option>
            </Select>
          </div>
          
          {sortBy === 'teams' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontWeight: 'bold' }}>Número de times:</label>
              <InputNumber
                min={2}
                max={10}
                value={numberOfTeams}
                onChange={setNumberOfTeams}
                style={{ width: window.innerWidth <= 768 ? 60 : 80 }}
              />
            </div>
          )}
          
          {sortBy === 'playersPerTeam' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontWeight: 'bold' }}>Jogadores por time:</label>
              <InputNumber
                min={3}
                max={15}
                value={playersPerTeam}
                onChange={setPlayersPerTeam}
                style={{ width: window.innerWidth <= 768 ? 60 : 80 }}
              />
            </div>
          )}
          
          <Button 
            type="primary" 
            onClick={handleSortTeams}
            loading={loadingSort}
            icon={<SwapOutlined />}
            size={window.innerWidth <= 768 ? 'small' : 'middle'}
            style={{ alignSelf: 'flex-end' }}
          >
            Sortear
          </Button>
        </div>

        {sortedTeams.length > 0 && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Row gutter={[16, 16]}>
                {sortedTeams.map((team, index) => (
                  <Col key={index} xs={24} sm={12} md={8}>
                    <Card 
                      title={`${team.name} (Média: ${team.average})`}
                      size="small"
                      style={{ marginBottom: 8 }}
                    >
                      {team.players.map((player, playerIndex) => (
                        <div key={playerIndex} style={{ marginBottom: 4 }}>
                          {player.name} ({player.technical_level})
                          {player.type === 2 && ' 🧤'}
                        </div>
                      ))}
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
            
            <div style={{ 
              backgroundColor: '#f5f5f5', 
              padding: 16, 
              borderRadius: 6, 
              marginBottom: 16,
              border: '1px solid #d9d9d9'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: 8,
                flexWrap: 'wrap',
                gap: 8
              }}>
                <Text strong>Formato para WhatsApp:</Text>
                <Button 
                  size="small"
                  onClick={handleCopyAllTeams}
                  icon={<CopyOutlined />}
                  style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    minWidth: 'auto',
                    padding: '4px 8px'
                  }}
                >
                  <span>Copiar</span>
                </Button>
              </div>
              <div style={{ 
                backgroundColor: 'white',
                padding: 12,
                borderRadius: 4,
                border: '1px solid #e8e8e8',
                fontFamily: 'monospace',
                fontSize: '12px',
                whiteSpace: 'pre-line',
                maxHeight: '200px',
                overflow: 'auto'
              }}>
                {formatTeamsForWhatsApp()}
              </div>
            </div>
            
            <div style={{ textAlign: 'center', marginTop: 16, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                size="large"
                icon={<AppstoreOutlined />}
                onClick={() => {
                  const gameDate = selectedGameForSort?.date_of_game || dayjs().format('DD/MM');
                  navigate('/escalacao', { state: { sortedTeams, gameDate, gameId: selectedGameForSort?.id } });
                  setSortModalVisible(false);
                }}
              >
                Escalar em campo
              </Button>
              <Button
                type="primary"
                size="large"
                onClick={handleSendToGroup}
              >
                Enviar no Grupo
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="Novo jogo"
        open={newGameModalVisible}
        onCancel={() => setNewGameModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <NewGameForm
          form={newGameForm}
          onFinish={handleCreateNewGame}
          onCancel={() => setNewGameModalVisible(false)}
          loading={newGameLoading}
          teamConfig={teamConfig}
        />
      </Modal>

      <Modal
        title="Editar jogo"
        open={editGameModal.open}
        onCancel={() => !editGameLoading && setEditGameModal({ open: false, game: null })}
        footer={null}
        destroyOnClose
      >
        {editGameModal.game && (
          <EditGameForm
            form={editGameForm}
            game={editGameModal.game}
            onFinish={submitEditGame}
            onCancel={() => setEditGameModal({ open: false, game: null })}
            loading={editGameLoading}
          />
        )}
      </Modal>

      <StatsInputModal
        open={statsModal.open}
        gameId={statsModal.gameId}
        gameLabel={statsModal.gameLabel}
        onClose={() => setStatsModal({ open: false, gameId: null, gameLabel: '' })}
      />

      <GameRatingsModal
        open={ratingsModal.open}
        gameId={ratingsModal.gameId}
        gameLabel={ratingsModal.gameLabel}
        onClose={() => setRatingsModal({ open: false, gameId: null, gameLabel: '' })}
      />

      <GameStatsBroadcastModal
        open={statsBroadcastModal.open}
        gameId={statsBroadcastModal.gameId}
        gameLabel={statsBroadcastModal.gameLabel}
        onClose={() => setStatsBroadcastModal({ open: false, gameId: null, gameLabel: '' })}
      />

      <RosterAdjustModal
        open={rosterAdjustModal.open}
        gameId={rosterAdjustModal.gameId}
        gameLabel={rosterAdjustModal.gameLabel}
        onClose={() => setRosterAdjustModal({ open: false, gameId: null, gameLabel: '' })}
        onSaved={() => { /* nada afeta lista pública — não precisa reload */ }}
      />

      {/* Modal de cancelamento — com opcao de notificar no grupo + justificativa */}
      <Modal
        title="Cancelar jogo"
        open={cancelModal.open}
        onCancel={() => !cancelSubmitting && setCancelModal({ open: false, gameId: null, notify: true, reason: '', cooldownInfo: null })}
        okText={cancelSubmitting ? 'Cancelando...' : (cancelModal.notify ? 'Cancelar + avisar no grupo' : 'Cancelar (sem avisar)')}
        cancelText="Voltar"
        okButtonProps={{
          danger: true,
          loading: cancelSubmitting,
          disabled: cancelSubmitting || (cancelModal.notify && !cancelModal.reason.trim()),
        }}
        onOk={async () => {
          const { gameId, notify, reason } = cancelModal;
          if (!user?.customer_id) return message.error('Erro: ID do cliente não encontrado. Faça login novamente.');
          setCancelSubmitting(true);
          try {
            // Se notify=true, envia msg no grupo primeiro (respeita cooldown)
            if (notify) {
              try {
                await api.post(`/organizer-games/${gameId}/cancel-notify`, { reason: reason.trim() });
              } catch (err) {
                const code = err?.response?.data?.error;
                if (code === 'cooldown') {
                  const msg = err.response.data.mensagem;
                  const proceed = window.confirm(`${msg}\n\nDeseja cancelar o jogo mesmo assim (sem avisar no grupo)?`);
                  if (!proceed) { setCancelSubmitting(false); return; }
                } else {
                  message.error(err?.response?.data?.mensagem || 'Erro ao enviar aviso no grupo');
                  setCancelSubmitting(false);
                  return;
                }
              }
            }
            // Cancela o jogo (webhook n8n existente)
            const response = await axios.post('https://automacao.mooblab.com/webhook/cancel-game', {
              game_id: gameId,
              customer_id: user.customer_id,
            }, {
              headers: { 'futezap-admin-api-token': ':Wu|_-1;)n0HJF)e@£5Mqm' },
            });
            if (response.data.success === 'true' || response.data.success === true) {
              message.success(notify ? 'Jogo cancelado e aviso enviado no grupo!' : 'Jogo cancelado.');
              setCancelModal({ open: false, gameId: null, notify: true, reason: '', cooldownInfo: null });
              fetchGames();
            } else {
              message.error(`Erro ao cancelar jogo: ${response.data.message || 'Resposta inesperada'}`);
            }
          } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || 'Erro desconhecido';
            message.error(`Erro ao cancelar jogo: ${errorMsg}`);
          } finally { setCancelSubmitting(false); }
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <Text>Tem certeza que deseja cancelar este jogo?</Text>
        </div>

        <div style={{ marginBottom: 12, padding: 12, background: '#fafafa', borderRadius: 8, border: '1px solid #eee' }}>
          <Switch
            checked={cancelModal.notify}
            onChange={(v) => setCancelModal(prev => ({ ...prev, notify: v }))}
            disabled={cancelSubmitting || (cancelModal.cooldownInfo && cancelModal.cooldownInfo.can_send === false)}
          />
          <Text strong style={{ marginLeft: 8 }}>Avisar a galera no grupo</Text>
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
            Se marcar, o Futezap manda uma msg no grupo do time explicando o cancelamento.
            {cancelModal.cooldownInfo && cancelModal.cooldownInfo.can_send === false && (
              <><br /><Text type="warning">
                ⚠️ Você já enviou uma notificação nas últimas 24h. Só pode enviar 1 por dia (esse jogo pode ser cancelado sem notificar).
              </Text></>
            )}
          </div>
        </div>

        {cancelModal.notify && (!cancelModal.cooldownInfo || cancelModal.cooldownInfo.can_send !== false) && (
          <div style={{ marginBottom: 12 }}>
            <Text strong>Motivo</Text>
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>(vai no grupo)</Text>
            <Input.TextArea
              value={cancelModal.reason}
              onChange={(e) => setCancelModal(prev => ({ ...prev, reason: e.target.value.slice(0, 500) }))}
              rows={3}
              maxLength={500}
              showCount
              placeholder="Ex: campo alagado, sem quorum, adiado pra próxima semana..."
              style={{ marginTop: 4 }}
              disabled={cancelSubmitting}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

// Extraído em subcomponente pra permitir Form.useWatch e validacoes reativas
// das datas de convocacao conforme o user muda cada campo.
function NewGameForm({ form, onFinish, onCancel, loading, teamConfig }) {
  void teamConfig;
  const dateOfGame = Form.useWatch('date_of_game', form);
  const earlyAt = Form.useWatch('early_call_at', form);
  const firstAt = Form.useWatch('first_call_at', form);
  const secondAt = Form.useWatch('second_call_at', form);

  const now = dayjs();

  // Faixa permitida: (agora, dateOfGame). Cada convocacao adiciona
  // constraint extra baseada na anterior (2ª > 1ª, 3ª > 2ª).
  const gameDate = dateOfGame && dayjs.isDayjs(dateOfGame) ? dateOfGame : null;

  const earlyInPast = earlyAt && dayjs(earlyAt).isBefore(now);
  const earlyAfterGame = earlyAt && gameDate && !dayjs(earlyAt).isBefore(gameDate);
  const firstInPast = firstAt && dayjs(firstAt).isBefore(now);
  const firstAfterGame = firstAt && gameDate && !dayjs(firstAt).isBefore(gameDate);
  const firstBeforeOrEqEarly = earlyAt && firstAt && !dayjs(firstAt).isAfter(dayjs(earlyAt));
  const secondInPast = secondAt && dayjs(secondAt).isBefore(now);
  const secondAfterGame = secondAt && gameDate && !dayjs(secondAt).isBefore(gameDate);
  const secondBeforeOrEqFirst = firstAt && secondAt && !dayjs(secondAt).isAfter(dayjs(firstAt));

  // Bloqueia datas invalidas no picker (a menor granularidade que o AntD
  // suporta sem custom render). Validacao fina cai nas msgs abaixo do campo.
  const disabledDateAfter = (upperBound) => (current) => {
    if (!current) return false;
    if (current.isBefore(now.startOf('day'))) return true;
    if (upperBound && !current.isBefore(upperBound.startOf('day').add(1, 'day'))) return true;
    return false;
  };

  const anyError = earlyInPast || earlyAfterGame
    || firstInPast || firstAfterGame || firstBeforeOrEqEarly
    || secondInPast || secondAfterGame || secondBeforeOrEqFirst;

  // Se preencher 2ª ou 3ª sem preencher a anterior, form dispara required
  const hasFirstWithoutEarly = firstAt && !earlyAt;
  const hasSecondWithoutFirst = secondAt && !firstAt;

  const callPickerStyle = { width: '100%' };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Form.Item
        name="date_of_game"
        label="Data e horário do jogo"
        rules={[{ required: true, message: 'Escolha a data e o horário' }]}
      >
        <DatePicker
          showTime={{ format: 'HH:mm', minuteStep: 15 }}
          format="DD/MM/YYYY HH:mm"
          style={{ width: '100%' }}
          placeholder="Escolha o dia e horário"
          disabledDate={(current) => current && current.isBefore(now.startOf('day'))}
        />
      </Form.Item>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="players" label="Nº de jogadores" rules={[{ required: true, message: 'Informe' }]}>
            <InputNumber min={1} max={40} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="goalkeepers" label="Nº de goleiros" rules={[{ required: true, message: 'Informe' }]}>
            <InputNumber min={0} max={10} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <div style={{ marginBottom: 8, marginTop: 8, fontWeight: 500 }}>
        Convocações (até 3) — escolhe a data e o horário exato de cada uma
      </div>

      {/* 1ª */}
      <div style={{ marginBottom: 4, fontSize: 13, color: '#666' }}>1ª convocação</div>
      <Form.Item
        name="early_call_at"
        rules={[{ required: true, message: 'Escolha a data da 1ª convocação' }]}
        validateStatus={(earlyInPast || earlyAfterGame) ? 'error' : undefined}
        style={{ marginBottom: 4 }}
      >
        <DatePicker
          showTime={{ format: 'HH:mm', minuteStep: 15 }}
          format="DD/MM/YYYY HH:mm"
          style={callPickerStyle}
          placeholder="Dia e horário"
          disabled={!gameDate}
          disabledDate={disabledDateAfter(gameDate)}
        />
      </Form.Item>
      <div style={{ marginBottom: 12, fontSize: 12 }}>
        {earlyInPast
          ? <Text type="danger">Cai no passado</Text>
          : earlyAfterGame
            ? <Text type="danger">Precisa ser antes do jogo</Text>
            : earlyAt
              ? <Text type="secondary">Sai em {dayjs(earlyAt).format('DD/MM/YYYY HH:mm')}</Text>
              : <Text type="secondary">Escolha data e horário</Text>}
      </div>

      {/* 2ª */}
      <div style={{ marginBottom: 4, fontSize: 13, color: '#666' }}>2ª convocação</div>
      <Form.Item
        name="first_call_at"
        rules={[{ required: true, message: 'Escolha a data da 2ª convocação' }]}
        validateStatus={(firstInPast || firstAfterGame || firstBeforeOrEqEarly || hasFirstWithoutEarly) ? 'error' : undefined}
        style={{ marginBottom: 4 }}
      >
        <DatePicker
          showTime={{ format: 'HH:mm', minuteStep: 15 }}
          format="DD/MM/YYYY HH:mm"
          style={callPickerStyle}
          placeholder="Dia e horário"
          disabled={!gameDate}
          disabledDate={disabledDateAfter(gameDate)}
        />
      </Form.Item>
      <div style={{ marginBottom: 16, fontSize: 12 }}>
        {firstInPast
          ? <Text type="danger">Cai no passado</Text>
          : firstAfterGame
            ? <Text type="danger">Precisa ser antes do jogo</Text>
            : firstBeforeOrEqEarly
              ? <Text type="danger">Precisa ser depois da 1ª</Text>
              : hasFirstWithoutEarly
                ? <Text type="danger">Preencha a 1ª antes</Text>
                : firstAt
                  ? <Text type="secondary">Sai em {dayjs(firstAt).format('DD/MM/YYYY HH:mm')}</Text>
                  : <Text type="secondary">Escolha data e horário</Text>}
      </div>

      {/* 3ª (opcional) */}
      <div style={{ marginBottom: 4, fontSize: 13, color: '#666' }}>
        3ª convocação <Text type="secondary" style={{ fontSize: 11 }}>(opcional)</Text>
      </div>
      <Form.Item
        name="second_call_at"
        validateStatus={(secondInPast || secondAfterGame || secondBeforeOrEqFirst || hasSecondWithoutFirst) ? 'error' : undefined}
        style={{ marginBottom: 4 }}
      >
        <DatePicker
          showTime={{ format: 'HH:mm', minuteStep: 15 }}
          format="DD/MM/YYYY HH:mm"
          style={callPickerStyle}
          placeholder="Dia e horário (deixa em branco pra não usar)"
          disabled={!gameDate}
          disabledDate={disabledDateAfter(gameDate)}
          allowClear
        />
      </Form.Item>
      <div style={{ marginBottom: 16, fontSize: 12 }}>
        {secondInPast
          ? <Text type="danger">Cai no passado</Text>
          : secondAfterGame
            ? <Text type="danger">Precisa ser antes do jogo</Text>
            : secondBeforeOrEqFirst
              ? <Text type="danger">Precisa ser depois da 2ª</Text>
              : hasSecondWithoutFirst
                ? <Text type="danger">Preencha a 2ª antes</Text>
                : secondAt
                  ? <Text type="secondary">Sai em {dayjs(secondAt).format('DD/MM/YYYY HH:mm')}</Text>
                  : <Text type="secondary">Deixe em branco pra pular</Text>}
      </div>

      <div style={{ textAlign: 'right' }}>
        <Button style={{ marginRight: 8 }} onClick={onCancel}>Cancelar</Button>
        <Button type="primary" htmlType="submit" loading={loading} disabled={anyError}>Criar jogo</Button>
      </div>
    </Form>
  );
}

// Modal de edicao de jogo avulso. Reusa toda a validacao visual do
// NewGameForm mas: (a) sem players/goalkeepers (nao muda no update),
// (b) bloqueia calls ja enviadas (sent_*=true) — organizador vê o
// horario ja disparado mas nao pode mexer.
function EditGameForm({ form, game, onFinish, onCancel, loading }) {
  const dateOfGame = Form.useWatch('date_of_game', form);
  const earlyAt = Form.useWatch('early_call_at', form);
  const firstAt = Form.useWatch('first_call_at', form);
  const secondAt = Form.useWatch('second_call_at', form);

  const now = dayjs();
  const gameDate = dateOfGame && dayjs.isDayjs(dateOfGame) ? dateOfGame : null;

  // Convocacao ja enviada = bloqueia edicao. Erros de validacao em call
  // ja enviada NAO contam pro anyError — dono nao consegue consertar
  // uma data que ja foi disparada, entao nao pode ser motivo pra bloquear
  // Salvar (ele quer editar OUTRAS coisas).
  const earlySent = !!game.sent_early_call;
  const firstSent = !!game.sent_first_call;
  const secondSent = !!game.sent_second_call;

  const earlyInPast = !earlySent && earlyAt && dayjs(earlyAt).isBefore(now);
  const earlyAfterGame = !earlySent && earlyAt && gameDate && !dayjs(earlyAt).isBefore(gameDate);
  const firstInPast = !firstSent && firstAt && dayjs(firstAt).isBefore(now);
  const firstAfterGame = !firstSent && firstAt && gameDate && !dayjs(firstAt).isBefore(gameDate);
  // Ordem cronologica só bloqueia se AMBAS podem ser editadas — se a
  // anterior ja foi enviada com data antiga, aceita.
  const firstBeforeOrEqEarly = !firstSent && earlyAt && firstAt && !dayjs(firstAt).isAfter(dayjs(earlyAt));
  const secondInPast = !secondSent && secondAt && dayjs(secondAt).isBefore(now);
  const secondAfterGame = !secondSent && secondAt && gameDate && !dayjs(secondAt).isBefore(gameDate);
  const secondBeforeOrEqFirst = !secondSent && firstAt && secondAt && !dayjs(secondAt).isAfter(dayjs(firstAt));

  const disabledDateAfter = (upperBound) => (current) => {
    if (!current) return false;
    if (current.isBefore(now.startOf('day'))) return true;
    if (upperBound && !current.isBefore(upperBound.startOf('day').add(1, 'day'))) return true;
    return false;
  };
  const disabledGameDate = (current) => current && current.isBefore(now.startOf('day'));

  const anyError = earlyInPast || earlyAfterGame
    || firstInPast || firstAfterGame || firstBeforeOrEqEarly
    || secondInPast || secondAfterGame || secondBeforeOrEqFirst;

  const hasFirstWithoutEarly = firstAt && !earlyAt;
  const hasSecondWithoutFirst = secondAt && !firstAt;

  const callPickerStyle = { width: '100%' };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      {(earlySent || firstSent || secondSent) && (
        <Alert
          type="info" showIcon
          style={{ marginBottom: 12 }}
          message="Uma ou mais convocações já foram enviadas"
          description="Convocações já enviadas ficam bloqueadas pra edição — só dá pra mudar as que ainda vão sair."
        />
      )}
      <Form.Item
        name="date_of_game"
        label="Data e horário do jogo"
        rules={[{ required: true, message: 'Escolha a data e o horário' }]}
      >
        <DatePicker
          showTime={{ format: 'HH:mm', minuteStep: 15 }}
          format="DD/MM/YYYY HH:mm"
          style={{ width: '100%' }}
          disabledDate={disabledGameDate}
        />
      </Form.Item>

      <div style={{ marginBottom: 8, marginTop: 8, fontWeight: 500 }}>
        Convocações
      </div>

      {/* 1ª */}
      <div style={{ marginBottom: 4, fontSize: 13, color: '#666' }}>
        1ª convocação {earlySent && <Tag color="green" style={{ marginLeft: 6 }}>Já enviada</Tag>}
      </div>
      <Form.Item
        name="early_call_at"
        rules={[{ required: true, message: 'Escolha a data da 1ª convocação' }]}
        validateStatus={(earlyInPast || earlyAfterGame) ? 'error' : undefined}
        style={{ marginBottom: 4 }}
      >
        <DatePicker
          showTime={{ format: 'HH:mm', minuteStep: 15 }}
          format="DD/MM/YYYY HH:mm"
          style={callPickerStyle}
          disabled={!gameDate || earlySent}
          disabledDate={disabledDateAfter(gameDate)}
        />
      </Form.Item>
      <div style={{ marginBottom: 12, fontSize: 12 }}>
        {earlySent
          ? <Text type="success">Enviada em {earlyAt ? dayjs(earlyAt).format('DD/MM/YYYY HH:mm') : '—'}</Text>
          : earlyInPast
            ? <Text type="danger">Cai no passado</Text>
            : earlyAfterGame
              ? <Text type="danger">Precisa ser antes do jogo</Text>
              : earlyAt
                ? <Text type="secondary">Sai em {dayjs(earlyAt).format('DD/MM/YYYY HH:mm')}</Text>
                : <Text type="secondary">Escolha data e horário</Text>}
      </div>

      {/* 2ª */}
      <div style={{ marginBottom: 4, fontSize: 13, color: '#666' }}>
        2ª convocação {firstSent && <Tag color="green" style={{ marginLeft: 6 }}>Já enviada</Tag>}
      </div>
      <Form.Item
        name="first_call_at"
        rules={[{ required: true, message: 'Escolha a data da 2ª convocação' }]}
        validateStatus={(firstInPast || firstAfterGame || firstBeforeOrEqEarly || hasFirstWithoutEarly) ? 'error' : undefined}
        style={{ marginBottom: 4 }}
      >
        <DatePicker
          showTime={{ format: 'HH:mm', minuteStep: 15 }}
          format="DD/MM/YYYY HH:mm"
          style={callPickerStyle}
          disabled={!gameDate || firstSent}
          disabledDate={disabledDateAfter(gameDate)}
        />
      </Form.Item>
      <div style={{ marginBottom: 16, fontSize: 12 }}>
        {firstSent
          ? <Text type="success">Enviada em {firstAt ? dayjs(firstAt).format('DD/MM/YYYY HH:mm') : '—'}</Text>
          : firstInPast
            ? <Text type="danger">Cai no passado</Text>
            : firstAfterGame
              ? <Text type="danger">Precisa ser antes do jogo</Text>
              : firstBeforeOrEqEarly
                ? <Text type="danger">Precisa ser depois da 1ª</Text>
                : hasFirstWithoutEarly
                  ? <Text type="danger">Preencha a 1ª antes</Text>
                  : firstAt
                    ? <Text type="secondary">Sai em {dayjs(firstAt).format('DD/MM/YYYY HH:mm')}</Text>
                    : <Text type="secondary">Escolha data e horário</Text>}
      </div>

      {/* 3ª (opcional) */}
      <div style={{ marginBottom: 4, fontSize: 13, color: '#666' }}>
        3ª convocação <Text type="secondary" style={{ fontSize: 11 }}>(opcional)</Text>
        {secondSent && <Tag color="green" style={{ marginLeft: 6 }}>Já enviada</Tag>}
      </div>
      <Form.Item
        name="second_call_at"
        validateStatus={(secondInPast || secondAfterGame || secondBeforeOrEqFirst || hasSecondWithoutFirst) ? 'error' : undefined}
        style={{ marginBottom: 4 }}
      >
        <DatePicker
          showTime={{ format: 'HH:mm', minuteStep: 15 }}
          format="DD/MM/YYYY HH:mm"
          style={callPickerStyle}
          disabled={!gameDate || secondSent}
          disabledDate={disabledDateAfter(gameDate)}
          allowClear={!secondSent}
        />
      </Form.Item>
      <div style={{ marginBottom: 16, fontSize: 12 }}>
        {secondSent
          ? <Text type="success">Enviada em {secondAt ? dayjs(secondAt).format('DD/MM/YYYY HH:mm') : '—'}</Text>
          : secondInPast
            ? <Text type="danger">Cai no passado</Text>
            : secondAfterGame
              ? <Text type="danger">Precisa ser antes do jogo</Text>
              : secondBeforeOrEqFirst
                ? <Text type="danger">Precisa ser depois da 2ª</Text>
                : hasSecondWithoutFirst
                  ? <Text type="danger">Preencha a 2ª antes</Text>
                  : secondAt
                    ? <Text type="secondary">Sai em {dayjs(secondAt).format('DD/MM/YYYY HH:mm')}</Text>
                    : <Text type="secondary">Deixe em branco pra pular</Text>}
      </div>

      <div style={{ textAlign: 'right' }}>
        <Button style={{ marginRight: 8 }} onClick={onCancel}>Cancelar</Button>
        <Button type="primary" htmlType="submit" loading={loading} disabled={anyError}>Salvar</Button>
      </div>
    </Form>
  );
}

export default OrganizerGames;